import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sparkles, Loader2, Volume2, Send, StopCircle } from 'lucide-react';
import { toast } from 'sonner';

function speak(text) {
  if (!window.speechSynthesis) { toast.error('Tu navegador no soporta voz'); return null; }
  window.speechSynthesis.cancel();
  const clean = (text || '').replace(/[#*_`>-]+/g, '').replace(/\s+/g, ' ').trim().slice(0, 800);
  const u = new SpeechSynthesisUtterance(clean);
  u.lang = 'es-ES';
  u.rate = 1.0; u.pitch = 1.1;
  const voices = window.speechSynthesis.getVoices();
  const es = voices.find(v => v.lang.startsWith('es'));
  if (es) u.voice = es;
  window.speechSynthesis.speak(u);
  return u;
}

/**
 * AI Chess Coach panel with conversation + voice.
 * Props: fen (required), last_move, pgn.
 */
export default function CoachPanel({ fen, last_move, pgn }) {
  const [messages, setMessages] = useState([]); // [{role, content}]
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [speaking, setSpeaking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  // Ensure voices loaded (Chrome quirk)
  useEffect(() => { if (window.speechSynthesis) window.speechSynthesis.getVoices(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  // Cancelar cualquier lectura TTS pendiente al desmontar el panel (ej.: al
  // cambiar de partida en Base de datos). Evita voces "zombie" de la partida
  // anterior mientras el nuevo CoachPanel se monta.
  useEffect(() => {
    return () => {
      try { window.speechSynthesis?.cancel(); } catch { /* silent */ }
    };
  }, []);

  const startExplanation = async () => {
    if (!fen) return;
    setLoading(true);
    try {
      const { data } = await api.post('/ai/coach', { fen, last_move, pgn, history: [] });
      setSessionId(data.session_id);
      setMessages([{ role: 'assistant', content: data.text || '' }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'El entrenador no está disponible');
    } finally { setLoading(false); }
  };

  const sendFollowUp = async (e) => {
    e?.preventDefault?.();
    const q = input.trim();
    if (!q) return;
    setInput('');
    const nextMsgs = [...messages, { role: 'user', content: q }];
    setMessages(nextMsgs);
    setLoading(true);
    try {
      const { data } = await api.post('/ai/coach', {
        fen, last_move, pgn, question: q,
        history: messages, session_id: sessionId
      });
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setMessages([...nextMsgs, { role: 'assistant', content: data.text || '' }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  };

  const readAloud = (text) => {
    if (speaking) { window.speechSynthesis?.cancel(); setSpeaking(false); return; }
    const u = speak(text);
    if (!u) return;
    setSpeaking(true);
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
  };

  const clear = () => { setMessages([]); setSessionId(null); window.speechSynthesis?.cancel(); setSpeaking(false); };

  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-violet-50 to-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-violet-600 text-white grid place-items-center">
            <Sparkles className="w-4 h-4"/>
          </div>
          <div className="font-display font-bold">Entrenador IA</div>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && <Button variant="ghost" size="sm" onClick={clear}>Limpiar</Button>}
          <Button data-testid="coach-ask-btn" onClick={startExplanation} disabled={loading || !fen} size="sm"
            className="rounded-full bg-violet-600 hover:bg-violet-700">
            {loading && messages.length === 0 ? <Loader2 className="w-4 h-4 animate-spin"/> : 'Explicar'}
          </Button>
        </div>
      </div>

      <div data-testid="coach-response" className="text-sm text-slate-700 leading-relaxed max-h-72 overflow-y-auto pr-1 space-y-3">
        {messages.length === 0 ? (
          <div className="text-slate-500">Pulsa <b>Explicar</b> y te explicaré esta posición. Luego puedes hacerme preguntas.</div>
        ) : messages.map((m, i) => (
          <div key={i} className={m.role === 'assistant' ? 'bg-white rounded-xl p-3 border border-violet-100' : 'text-right'}>
            {m.role === 'assistant' ? (
              <>
                <div className="whitespace-pre-wrap">{m.content}</div>
                <div className="mt-2 flex justify-end">
                  <Button data-testid={`coach-tts-${i}`} onClick={() => readAloud(m.content)} variant="ghost" size="sm" className="h-7 px-2 text-violet-700 hover:bg-violet-100">
                    {speaking ? <><StopCircle className="w-3.5 h-3.5 mr-1"/> Parar</> : <><Volume2 className="w-3.5 h-3.5 mr-1"/> Escuchar</>}
                  </Button>
                </div>
              </>
            ) : (
              <div className="inline-block bg-violet-600 text-white rounded-2xl rounded-br-sm px-3 py-1.5 text-sm max-w-[85%]">{m.content}</div>
            )}
          </div>
        ))}
        {loading && messages.length > 0 && (
          <div className="text-slate-500 text-xs">Entrenador pensando…</div>
        )}
        <div ref={endRef}/>
      </div>

      {messages.length > 0 && (
        <form onSubmit={sendFollowUp} className="mt-3 flex gap-2">
          <Input data-testid="coach-input" placeholder="Pregúntame algo… (ej: ¿por qué e4 y no d4?)"
            value={input} onChange={(e)=>setInput(e.target.value)} disabled={loading}/>
          <Button data-testid="coach-send-btn" type="submit" disabled={loading || !input.trim()} size="icon" className="rounded-full bg-violet-600 hover:bg-violet-700">
            <Send className="w-4 h-4"/>
          </Button>
        </form>
      )}
    </div>
  );
}
